import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import i18n from "@/lib/i18n/i18n";
import { supabase } from "@/integrations/supabase/client";
import {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  LocaleCode,
  isRTL,
  getLocaleConfig,
  LOCALE_COOKIE_NAME,
  LOCALE_STORAGE_KEY,
  LOCALE_COOKIE_MAX_AGE,
} from "@/lib/i18n/locales";
import "@/lib/i18n/i18n";

interface LocaleContextType {
  locale: LocaleCode;
  dir: "ltr" | "rtl";
  setLocale: (locale: LocaleCode) => Promise<void>;
  isLoading: boolean;
  supportedLocales: typeof SUPPORTED_LOCALES;
  getLocaleLabel: (code: string) => string;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

// Cookie helpers
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

function setCookie(name: string, value: string, maxAge: number) {
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

// Resolve initial locale - always default to English
function resolveInitialLocale(): LocaleCode {
  // 1. Check cookie
  const cookieLocale = getCookie(LOCALE_COOKIE_NAME);
  if (cookieLocale && SUPPORTED_LOCALES.some((l) => l.code === cookieLocale)) {
    return cookieLocale as LocaleCode;
  }

  // 2. Check localStorage
  const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (storedLocale && SUPPORTED_LOCALES.some((l) => l.code === storedLocale)) {
    return storedLocale as LocaleCode;
  }

  // 3. Always default to English (do not detect browser locale)
  return DEFAULT_LOCALE;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>(DEFAULT_LOCALE);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  // Apply locale attributes to document and i18next
  const applyLocaleToDocument = useCallback((loc: LocaleCode) => {
    const dir = isRTL(loc) ? "rtl" : "ltr";
    document.documentElement.lang = loc;
    document.documentElement.dir = dir;
    i18n.changeLanguage(loc);
  }, []);

  // Initialize locale on mount
  useEffect(() => {
    const initialLocale = resolveInitialLocale();
    setLocaleState(initialLocale);
    applyLocaleToDocument(initialLocale);
    setIsLoading(false);
  }, [applyLocaleToDocument]);

  // Listen to auth state changes directly from supabase
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUserId(session?.user?.id ?? null);
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Sync with user's DB preference when authenticated
  useEffect(() => {
    if (!userId) return;

    const fetchUserLocale = async () => {
      const { data: profile } = await supabase
        .from("profiles")
        .select("preferred_language")
        .eq("id", userId)
        .single();

      if (profile?.preferred_language) {
        const userLocale = profile.preferred_language as LocaleCode;
        if (SUPPORTED_LOCALES.some((l) => l.code === userLocale)) {
          setLocaleState(userLocale);
          applyLocaleToDocument(userLocale);
          // Sync to cookie/storage
          setCookie(LOCALE_COOKIE_NAME, userLocale, LOCALE_COOKIE_MAX_AGE);
          localStorage.setItem(LOCALE_STORAGE_KEY, userLocale);
        }
      }
    };

    fetchUserLocale();
  }, [userId, applyLocaleToDocument]);

  // Set locale and persist
  const setLocale = useCallback(
    async (newLocale: LocaleCode) => {
      setLocaleState(newLocale);
      applyLocaleToDocument(newLocale);

      // Persist to cookie and localStorage
      setCookie(LOCALE_COOKIE_NAME, newLocale, LOCALE_COOKIE_MAX_AGE);
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);

      // Persist to DB if authenticated
      if (userId) {
        await supabase
          .from("profiles")
          .update({ preferred_language: newLocale })
          .eq("id", userId);
      }
    },
    [userId, applyLocaleToDocument]
  );

  const getLocaleLabel = useCallback((code: string) => {
    return getLocaleConfig(code).nativeName;
  }, []);

  const dir = isRTL(locale) ? "rtl" : "ltr";

  return (
    <LocaleContext.Provider
      value={{
        locale,
        dir,
        setLocale,
        isLoading,
        supportedLocales: SUPPORTED_LOCALES,
        getLocaleLabel,
      }}
    >
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return context;
}

/**
 * Safe version of useLocale that returns null if LocaleProvider is not available.
 * Use this in components that might render outside the provider tree.
 */
export function useLocaleSafe() {
  return useContext(LocaleContext);
}

// Export the context for direct access in edge cases
export { LocaleContext };
